import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../authContext';
import { toast } from 'react-toastify';
import axios from '../lib/axios';

const DEFAULT_AVATAR =
  'https://icon-library.com/images/anonymous-avatar-icon/anonymous-avatar-icon-25.jpg';
const OTP_PLACEHOLDER_EMAIL_SUFFIX = '@otp.indorinama.invalid';
const OTP_PLACEHOLDER_NAME_PREFIX = 'Indorinama User';
const OTP_PLACEHOLDER_CITY = 'unknown';

const INITIAL_FORM_DATA = {
  name: '',
  gender: '',
  dob: '',
  email: '',
  phone: '',
  city: '',
  address: '',
  avatar: null,
};

const getProfilePayload = (responseData) =>
  responseData?.user ?? responseData?.doc ?? responseData ?? null;

const normalizeEmail = (value = '') => value.trim().toLowerCase();

const isOtpPlaceholderEmail = (value = '') =>
  normalizeEmail(value).endsWith(OTP_PLACEHOLDER_EMAIL_SUFFIX);

const isOtpPlaceholderName = (value = '') =>
  value.trim().startsWith(OTP_PLACEHOLDER_NAME_PREFIX);

const isOtpPlaceholderCity = (value = '') =>
  value.trim().toLowerCase() === OTP_PLACEHOLDER_CITY;

const getProfileFieldValue = (value, isPlaceholder) => (isPlaceholder(value) ? '' : value || '');

const normalizeDateInput = (value) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toISOString().split('T')[0];
};

const resolveMediaURL = (media) => {
  const source =
    media?.url ||
    media?.thumbnailURL ||
    media?.cloudinary?.secure_url ||
    media ||
    '';

  if (!source || typeof source !== 'string') return null;
  if (/^https?:\/\//i.test(source)) return source;

  try {
    return new URL(source, process.env.REACT_APP_BACKEND_URL).toString();
  } catch (error) {
    return source;
  }
};

const MyInfo = () => {
  const { user, accessToken, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [profileId, setProfileId] = useState(user?.id || null);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const memberSince = useMemo(() => {
    if (!user?.createdAt) return null;

    return new Date(user.createdAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [user?.createdAt]);

  const hydrateProfile = (profile) => {
    if (!profile) return;

    setProfileId(profile.id || profile._id || null);
    setFormData({
      name: getProfileFieldValue(profile.name, isOtpPlaceholderName),
      gender: profile.gender || '',
      dob: normalizeDateInput(profile.dob),
      email: getProfileFieldValue(profile.email, isOtpPlaceholderEmail),
      phone: profile.phone || '',
      city: getProfileFieldValue(profile.city, isOtpPlaceholderCity),
      address: profile.address || '',
      avatar: null,
    });
    setAvatarPreview(resolveMediaURL(profile.avatar));
  };

  const fetchUserProfile = useCallback(async () => {
    if (!accessToken) {
      setPageLoading(false);
      return;
    }

    try {
      setPageLoading(true);
      axios.clearCache?.();

      const response = await axios.get('/api/users/me', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const profile = getProfilePayload(response.data);
      hydrateProfile(profile);
    } catch (error) {
      toast.error('Failed to fetch user profile');
    } finally {
      setPageLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  useEffect(() => {
    return () => {
      if (avatarPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (avatarPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(avatarPreview);
    }

    setFormData((prev) => ({
      ...prev,
      avatar: file,
    }));
    setAvatarPreview(URL.createObjectURL(file));
  };

  const uploadAvatarIfNeeded = async () => {
    if (!(formData.avatar instanceof File)) return null;

    const uploadData = new FormData();
    uploadData.append('file', formData.avatar);
    uploadData.append('alt', `${formData.name || 'User'} avatar`);

    const response = await axios.post('/api/media', uploadData, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const uploadedMedia = response.data?.doc ?? response.data;
    return uploadedMedia?.id || null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!profileId) {
      toast.error('Unable to update profile right now.');
      return;
    }

    try {
      setLoading(true);

      const avatarId = await uploadAvatarIfNeeded();
      const payload = {
        name: formData.name.trim(),
        gender: formData.gender || null,
        dob: formData.dob || null,
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        city: formData.city.trim(),
        address: formData.address.trim(),
        ...(avatarId ? { avatar: avatarId } : {}),
      };

      const response = await axios.patch(`/api/users/${profileId}`, payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const updatedProfile = getProfilePayload(response.data);
      axios.clearCache?.();

      if (updatedProfile) {
        hydrateProfile(updatedProfile);
        updateUser({
          ...user,
          ...updatedProfile,
        });
      } else {
        await fetchUserProfile();
      }

      toast.success('Profile updated successfully');
      setIsEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-red-600"></div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <h2 className="text-2xl font-bold text-gray-900">My Profile</h2>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <img
                      src={avatarPreview || DEFAULT_AVATAR}
                      alt="Profile"
                      className="h-20 w-20 rounded-full object-cover border-2 border-gray-200"
                    />
                    {isEditing && (
                      <label
                        htmlFor="avatar-upload"
                        className="absolute bottom-0 right-0 bg-red-600 text-white rounded-full p-1 cursor-pointer hover:bg-red-700"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <input
                          type="file"
                          id="avatar-upload"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (isEditing) {
                        fetchUserProfile();
                        setIsEditing(false);
                        return;
                      }

                      setIsEditing(true);
                    }}
                    className="text-red-600 hover:text-red-800 font-medium"
                    type="button"
                  >
                    {isEditing ? 'Cancel' : 'Edit Profile'}
                  </button>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 ${
                      isEditing
                        ? 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 ${
                      isEditing
                        ? 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 ${
                      isEditing
                        ? 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 ${
                      isEditing
                        ? 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 ${
                      isEditing
                        ? 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 ${
                      isEditing
                        ? 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Address
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  rows="3"
                  className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 ${
                    isEditing
                      ? 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                />
              </div>

              {isEditing && (
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Updating...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              )}
            </form>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Account Information
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                {memberSince && (
                  <p>
                    <span className="font-medium">Member since:</span>{' '}
                    {memberSince}
                  </p>
                )}
                <p>
                  <span className="font-medium">Account Status:</span>{' '}
                  <span className="text-green-600">Active</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MyInfo;
